import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import HomeEntryPoint, {
	type HomeEntryPointProps,
} from '@databox/ember-integrations/lib/components/Home/HomeEntryPoint';
import type { ChatImage } from '@databox/types/domains/ai/chat-image';
import { AiTrackingOrigin, type ChatMode } from '@databox/types/domains/ai/chat.enums';
import type { ContextObjectBase } from '@databox/types/domains/ai/context';
import type { MetricSetup } from '@databox/types/domains/metric-setup/metric-setup';

import type {
	APIData,
	DateRangeInterface,
	PopupData,
	ReactIntegrations,
	Service,
} from 'webapp-types';
import type { BlockVisualization } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode, redirect } from 'webapp/utils/common';
import { toReactMetricSetup } from 'webapp/utils/home';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

import MetricSetupModel from 'webapp/models-custom/metric-setup';

type MetricInsightsRequest = Parameters<HomeEntryPointProps['host']['onOpenMetricInsights']>[0];

// Home omits dateRange when the BFF can't resolve it; the drawer then uses its default range.
type HomeMetricInsights = Omit<
	APIData.GenerativeAI.AiChat.MetricInsightsDrawerAction,
	'dateRange' | 'metricSetup'
> &
	Partial<Pick<APIData.GenerativeAI.AiChat.MetricInsightsDrawerAction, 'dateRange'>> & {
		metricSetup: MetricSetup;
	};

interface HomePageSignature {
	Element: HTMLDivElement;
}

export default class HomePageComponent extends Component<HomePageSignature> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('react') declare reactService: Service.React;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	reactElement: ReactIntegrations.Root = null;

	constructor(owner: Owner, args: object) {
		super(owner, args);

		if (
			!this.navigationService.isMobileApp &&
			this.userSettingsService.showInvitedUserIntroVideo
		) {
			this.openIntroTourVideoPopup();
			this.userSettingsService.showInvitedUserIntroVideo = false;
		}
	}

	private openIntroTourVideoPopup(): void {
		const url = 'https://player.vimeo.com/video/1226554417?h=f0eaae229b&autoplay=1';
		this.navigationService.openVideoPopup(url);
	}

	@action
	onUserNameChange() {
		this.renderHome();
	}

	@action
	setupView(node: HTMLElement) {
		mountReactRoot(this, node, () => this.renderHome());
	}

	@action
	teardownView() {
		unmountReactRoot(this);

		// TEMPORARY WORKAROUND, as in genie/full-screen/index.gts: Home's nice-modal DialogProvider
		// breaks connect's showDialog() on unmount; themeChanged re-renders connect to recover.
		this.eventManagerService.trigger(AppEvent.THEME_CHANGED);
	}

	// --- HomeHost: pickers. The Ember popups reject on cancel; React expects a resolved null. ---

	@action
	async pickMetric(
		current?: MetricSetup,
	): Promise<{ metricSetup: MetricSetup; name: string | null } | null> {
		// The picked metric's name is a sync cache lookup, like model.metric in openMetricInsights.
		await this.mmMetricsService.requestMetrics();

		const options: Omit<PopupData.Common.MetricSelection, 'onSave'> = {
			// The shared type allows key-less (ad-hoc) setups; the Ember picker can only preselect a keyed one.
			metricSetupData: current?.key ? { ...current, key: current.key } : undefined,
			requireSingleOutput: true,
			// Breakdown and filters are the React edit dialog's job, so skip the second Ember popup.
			skipAdditionalProperties: true,
		};

		return this.mmDataSelectsService.openMetricSelectWithAttributes(options).then(
			(data) => {
				const metricSetup = toReactMetricSetup(data);

				if (!metricSetup) {
					return null;
				}

				return { metricSetup, name: new MetricSetupModel(data).metric?.name ?? null };
			},
			() => null,
		);
	}

	@action
	async pickDataboard(): Promise<number | null> {
		// The mobile app doesn't load boards on boot.
		if (this.navigationService.isMobileApp) {
			await this.mmBoardsService.requestBoards();
		}

		return new Promise((resolve) => {
			const popup = this.mmBoardsService.createBoardsSelectListModal();

			popup.open({
				model: this.mmBoardsService.normalBoardModels,
				maxSelectable: 1,
				title: 'Select dashboard',
				emptyState: {
					header: 'No dashboards created yet',
					text: 'To add a dashboard to your Home, you need to build dashboards first.',
					ctaLabel: 'Build dashboards',
					ctaRoute: 'boards',
				},
				maxSelectableError: 'Only one dashboard can be selected!',
				onConfirm: (selectedBoardIds: number[]) => {
					resolve(selectedBoardIds[0] ?? null);
					return Promise.resolve(true);
				},
				onCancel: () => resolve(null),
			});
		});
	}

	@action
	async pickReport(): Promise<number | null> {
		// Reports aren't loaded on boot.
		await this.mmReportsService.requestReports();

		return new Promise((resolve) => {
			const popup = this.mmBoardsService.createBoardsSelectListModal();

			popup.open({
				model: [...this.mmReportsService.normalReportModels],
				maxSelectable: 1,
				isForSelectingReports: true,
				title: 'Select report',
				emptyState: {
					header: 'No reports created yet',
					text: 'To add a report to your Home, you need to build reports first.',
					ctaLabel: 'Build reports',
					ctaRoute: 'reports',
				},
				maxSelectableError: 'Only one report can be selected!',
				onConfirm: (_selectedBoardIds: number[], selectedItemObjects) => {
					// selectedBoardIds is always empty in report mode - the picked report lives in
					// selectedItemObjects instead.
					const [picked] = selectedItemObjects;
					resolve(picked?.objectId ?? null);
					return Promise.resolve(true);
				},
				onCancel: () => resolve(null),
			});
		});
	}

	@action
	pickGoal(): Promise<number | null> {
		return this.mmGoalsService.pickGoal();
	}

	@action
	pickClient(): Promise<number | null> {
		return this.mmSpacesService.pickClient();
	}

	// --- HomeHost: navigation ---

	@action
	openDataboard(boardId: number): void {
		const board = this.mmBoardsService.findModel(String(boardId));

		if (!board || board.deleted) {
			this.aiChatService.showToast('error', 'Dashboard is not available');
			return;
		}

		this.mmBoardsService.openPreview(board, AiTrackingOrigin.HOME);
	}

	@action
	async openReport(reportId: number): Promise<void> {
		// Reports aren't loaded on boot; see pickReport.
		await this.mmReportsService.requestReports();

		const report = this.mmReportsService.findModel(String(reportId));

		if (!report || report.deleted) {
			this.aiChatService.showToast('error', 'Report is not available');
			return;
		}

		this.mmReportsService.openPreviewPopup(report, AiTrackingOrigin.HOME);
	}

	@action
	async openGoal(goalId: number): Promise<void> {
		if (!(await this.mmGoalsService.openGoalInsightsDrawerById(goalId))) {
			this.aiChatService.showToast('error', 'Goal is not available');
		}
	}

	// A full page load into the client's space. It lands where the account switcher does: an agency user
	// in a client space has no Home, so on Databoards.
	@action
	openClient(clientId: number): void {
		const space = this.mmSpacesService.getBySpaceId(String(clientId));

		// A hidden space is one this user can't access; switching to it would bounce back silently.
		if (!space || space.hidden) {
			this.aiChatService.showToast('error', 'Client is not available');
			return;
		}

		redirect(`${space.loginUrl}?redirect_path=${encodeURIComponent('/home')}`);
	}

	@action
	openActivityLog(): void {
		this.navigationService.transitionToRoute('settings.logs');
	}

	@action
	openDataManager(filter?: 'error'): void {
		void this.navigationService.transitionToRoute('datamanager.connected').then(() => {
			// Data Manager keeps its filters in the hash (data source|status|creator); a demo account ignores it.
			if (filter === 'error') {
				window.location.hash = '|Error';
			}
		});
	}

	@action
	async openMetricInsights({
		metricSetup,
		dateRange,
		visualization,
	}: HomeMetricInsights): Promise<void> {
		// model.metric is a sync cache lookup; a KPI click can come before any picker fills it.
		await this.mmMetricsService.requestMetrics();

		// The drawer won't open without a metric.
		const model = new MetricSetupModel(metricSetup);

		if (!model.metric) {
			this.aiChatService.showToast('error', 'Metric is not available');
			return;
		}

		this.metricDetailsService.openMetricDetailsDrawer(
			{ metricSetup: model, dateRange, visualization },
			'home',
		);
	}

	@action
	startThread(
		message: string,
		mentionContext?: ContextObjectBase[],
		selectedAccountIds?: number[],
		images?: ChatImage[],
		chatMode?: ChatMode,
	): void {
		this.aiChatService.openGenieWithPrompt(
			message,
			AiTrackingOrigin.HOME,
			mentionContext,
			selectedAccountIds,
			images,
			chatMode,
		);
	}

	// Built once: React Home keeps host as a context value and callback dependency.
	@cached
	get host(): HomeEntryPointProps['host'] {
		return {
			onPickMetric: this.pickMetric,
			onPickDataboard: this.pickDataboard,
			onPickReport: this.pickReport,
			onPickGoal: this.pickGoal,
			onPickClient: this.pickClient,
			onOpenDataboard: (boardId: number) => {
				void this.openDataboard(boardId);
			},
			onOpenReport: (reportId: number) => {
				void this.openReport(reportId);
			},
			onOpenGoal: (goalId: number) => {
				void this.openGoal(goalId);
			},
			onOpenClient: this.openClient,
			onOpenActivityLog: this.openActivityLog,
			onOpenDataManager: this.openDataManager,
			onOpenMetricInsights: (insights: MetricInsightsRequest) => {
				// The same BFF payload the AI chat's OPEN_METRIC_INSIGHTS action carries: @databox/types types its dates as
				// plain strings and its Visualization numbers are the webapp's BlockVisualization ones.
				void this.openMetricInsights({
					...insights,
					dateRange: insights.dateRange as
						DateRangeInterface.FormulaInterface | undefined,
					visualization: insights.visualization as BlockVisualization | undefined,
				});
			},
			onOpenThread: this.aiChatService.openThread,
			// A Recents run opens its run page, which is what marks the run read.
			onOpenRun: this.aiChatService.onNavigateToRun,
			// Opens the Settings dialog; the composer's "/" menu shows its Manage / Browse skills rows only with it.
			onNavigateToSettings: this.aiChatService.onNavigateToSettings,
			onStartThread: this.startThread,
			onUpgrade: this.aiChatService.onUpgrade,
			onAddCredits: this.aiChatService.onAddCredits,
			showToast: this.aiChatService.showToast,
			onIsChangeRestricted: () => this.accountService.handleDemo(),
			// A predicate, so the cached host still reads the current spaces.
			canUseClients: () => this.mmSpacesService.hasClientsToPick,
			// The same checks as Framework and settings.logs (anyone else gets a 403 card). This space's
			// isAdmin on purpose: the profile menu's isAdminButNotInClientSpace would hide a tile that works.
			canUseActivityLog: () =>
				this.currentUserService.isAdmin && this.billingService.activityLogEnabled,
		};
	}

	get entryPointProps(): HomeEntryPointProps {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		return {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken ?? '',
			dataToken: dataToken ?? '',
			spaceId: this.accountService.id,
			user: {
				id: this.currentUserService.numericId,
				name: this.currentUserService.displayName,
			},
			// Same gate as the Genie route: plans without the AI assistant still get the section.
			isAiEnabled: this.aiChatService.isAiChatAvailable,
			// featureConfig holds the composer's upgrade gate; demo plans trip it even if eligible.
			featureConfig: this.aiChatService.featureConfig,
			trackingOrigin: AiTrackingOrigin.HOME,
			// The native app renders widgets inert and hides customize; it has no pages to open them on.
			isMobileApp: this.navigationService.isMobileApp,
			host: this.host,
			// run.completed refreshes Home's Recents.
			subscribeToAiEvents: this.aiChatService.subscribeToAiEvents,
		};
	}

	private renderHome() {
		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				HomeEntryPoint(this.entryPointProps),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{didUpdate this.onUserNameChange this.currentUserService.displayName}}
			{{willDestroy this.teardownView}}
			id="home-page-container"
			...attributes
		/>
	</template>
}
